import React, { useMemo, useRef, useState } from 'react';
import './index.css';
import { handleForbiddenResponse } from '../../utils/api';
import {SearchIcon} from "../shared/SearchIcon";
import Iphone from "../../assets/iPhone 14 Pro.webp"
import Explosion from "../../assets/Explosion.webp"

const API_URL = 'https://tkachev-trade.com/api/ai/analyze';
const GENERIC_ERROR = 'Failed to analyze the image. Please try again.';
const DEFAULT_PROMPT = ' ';

const extractErrorMessage = (payload) => {
  if (!payload) {
    return GENERIC_ERROR;
  }
  if (Array.isArray(payload.errors)) {
    const normalizedError = payload.errors.find((item) => typeof item === 'string' && item.trim());
    if (normalizedError) {
      return normalizedError;
    }
  }
  if (typeof payload.message === 'string' && payload.message.trim()) {
    return payload.message.trim();
  }
  if (typeof payload.error === 'string' && payload.error.trim()) {
    return payload.error.trim();
  }
  return GENERIC_ERROR;
};

const createInlineNodes = (text, keyPrefix) => {
  if (typeof text !== 'string' || text.length === 0) {
    return [];
  }

  const nodes = [];
  const regex = /(\*\*([^*]+)\*\*|__([^_]+)__|\*([^*]+)\*|_([^_]+)_|`([^`]+)`)/g;
  let lastIndex = 0;
  let match;
  let keyIndex = 0;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }

    const boldMatch = match[2] || match[3];
    const italicMatch = match[4] || match[5];
    const codeMatch = match[6];

    if (boldMatch) {
      nodes.push(
        <strong key={`${keyPrefix}-strong-${keyIndex++}`}>{boldMatch}</strong>
      );
    } else if (italicMatch) {
      nodes.push(
        <em key={`${keyPrefix}-em-${keyIndex++}`}>{italicMatch}</em>
      );
    } else if (codeMatch) {
      nodes.push(
        <code key={`${keyPrefix}-code-${keyIndex++}`}>{codeMatch}</code>
      );
    }

    lastIndex = match.index + match[0].length;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes;
};

const renderMarkdown = (markdown) => {
  if (typeof markdown !== 'string' || !markdown.trim()) {
    return [];
  }

  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  const elements = [];
  let listBuffer = [];
  let paragraphLines = [];
  let elementIndex = 0;
  let inCodeBlock = false;
  let codeLines = [];
  let codeLanguage = '';

  const flushParagraph = () => {
    if (paragraphLines.length === 0) {
      return;
    }
    const paragraphText = paragraphLines.join(' ').replace(/\s+/g, ' ').trim();
    paragraphLines = [];
    if (!paragraphText) {
      return;
    }
    const inlineNodes = createInlineNodes(paragraphText, `p-${elementIndex}`);
    elements.push(
      <p key={`p-${elementIndex++}`}>{inlineNodes}</p>
    );
  };

  const flushList = () => {
    if (listBuffer.length === 0) {
      return;
    }
    const listKey = `list-${elementIndex++}`;
    elements.push(
      <ul key={listKey}>
        {listBuffer.map((item, itemIndex) => {
          const inlineNodes = createInlineNodes(item, `${listKey}-item-${itemIndex}`);
          return (
            <li key={`${listKey}-item-${itemIndex}`}>
              {inlineNodes}
            </li>
          );
        })}
      </ul>
    );
    listBuffer = [];
  };

  const flushCodeBlock = () => {
    if (!inCodeBlock) {
      return;
    }
    const codeKey = `code-${elementIndex++}`;
    elements.push(
      <pre key={codeKey}>
        <code data-language={codeLanguage || undefined}>
          {codeLines.join('\n')}
        </code>
      </pre>
    );
    inCodeBlock = false;
    codeLines = [];
    codeLanguage = '';
  };

  lines.forEach((rawLine) => {
    const trimmedLine = rawLine.trim();

    if (inCodeBlock) {
      if (trimmedLine.startsWith('```')) {
        flushCodeBlock();
      } else {
        codeLines.push(rawLine);
      }
      return;
    }

    if (trimmedLine.startsWith('```')) {
      flushParagraph();
      flushList();
      inCodeBlock = true;
      codeLanguage = trimmedLine.slice(3).trim();
      codeLines = [];
      return;
    }

    if (!trimmedLine) {
      flushParagraph();
      flushList();
      return;
    }

    const headingMatch = trimmedLine.match(/^(#{1,6})\s+(.*)$/);
    if (headingMatch) {
      flushParagraph();
      flushList();
      const level = Math.min(headingMatch[1].length, 6);
      const content = headingMatch[2];
      const inlineNodes = createInlineNodes(content, `h-${elementIndex}`);
      elements.push(
        React.createElement(`h${level}`, { key: `h-${elementIndex++}` }, ...inlineNodes)
      );
      return;
    }

    if (/^[-*+]\s+/.test(trimmedLine)) {
      flushParagraph();
      const itemContent = trimmedLine.replace(/^[-*+]\s+/, '').trim();
      listBuffer.push(itemContent);
      return;
    }

    paragraphLines.push(trimmedLine);
  });

  flushParagraph();
  flushList();
  flushCodeBlock();

  return elements;
};

export const ChatGptContainer = () => {
  const [markdown, setMarkdown] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef(null);

  const renderedMarkdown = useMemo(() => renderMarkdown(markdown), [markdown]);
  const shouldShowPlaceholder = !isLoading && !error && !markdown;

  const handleAddImageClick = () => {
    if (isLoading) {
      return;
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) {
      return;
    }

    setIsLoading(true);
    setError('');
    setMarkdown('');

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication token is missing.');
      }

      const formData = new FormData();
      formData.append('image', file);
      formData.append('prompt', DEFAULT_PROMPT);

      const response = await fetch(API_URL, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: '*/*',
        },
        body: formData,
      });

      if (handleForbiddenResponse(response)) {
        return;
      }

      let payload;
      try {
        payload = await response.json();
      } catch (parseError) {
        throw new Error(GENERIC_ERROR);
      }

      if (!response.ok || payload?.status !== 1) {
        throw new Error(extractErrorMessage(payload));
      }

      const generation = payload?.response?.generation;
      if (typeof generation !== 'string' || !generation.trim()) {
        throw new Error('Analysis response is empty.');
      }

      setMarkdown(generation);
    } catch (uploadError) {
      const message = uploadError instanceof Error ? uploadError.message : GENERIC_ERROR;
      setError(message);
    } finally {
      setIsLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
      <div className="chatgpt-container">
        <h2 className="chatgpt-heading">CRYPTO GPT</h2>
          {isLoading && (
            <div className="chatgpt-loading flex-1">
              <span className="chatgpt-spinner" aria-hidden="true" />
              <span>Analyzing chart...</span>
            </div>
          )}
          {!isLoading && error && (
            <div className="chatgpt-error">{error}</div>
          )}
          {!isLoading && !error && markdown && (
            <div className="chatgpt-markdown">
              {renderedMarkdown}
            </div>
          )}
          {shouldShowPlaceholder && (
              <div
                  className={"h-[70dvh] w-full flex justify-center overflow-hidden relative "}>
                <div
                    className={"flex flex-col items-center text-center gap-4 pt-10"}>
                  <div className={"w-[80%] text-[14px]"}>
                    Crypto GPT создан для анализа графиков криптовалют. Чтобы получить
                    прогноз,
                    загрузите скриншот графика
                  </div>
                  <div className={"w-[80%] text-[14px]"}>
                    Важно: бот работает только со скриншотами графиков, другие изображения
                    или
                    данные не принимаются.
                  </div>
                  <div className={"w-[80%]  text-[14px]"}>
                    Решения о сделках принимаете вы.
                    Бот — это инструмент для помощи,
                    а не финансовый советчик
                  </div>
                </div>


                <img src={Iphone} alt={"phone"}
                     className={"max-h-[532px] w-auto shrink-0 absolute -bottom-[25dvh] z-10"}/>

              </div>
          )}


        {!markdown && <img src={Explosion} alt={"Explosion"}
              className={"absolute -top-[20dvh] h-[140dvh] right-0"}/>}
        <input
            ref={fileInputRef}
            className="chatgpt-file-input z-10"
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            hidden
        />
        <button
            type="button"
            className="chatgpt-upload-button w-full flex items-center justify-center gap-4 z-10"
            onClick={handleAddImageClick}
            disabled={isLoading}
        >
          <SearchIcon/>

          Анализировать
        </button>
      </div>
  );
};
